import type { BoxPlotPoint, ScatterPoint, StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import _ from 'lodash';
import { sqFormulasApi } from '@/sdk/api/FormulasApi';
import {
  BOXPLOT_LABEL_Z_INDEX,
  CURSOR_Y_OFFSET,
  SCATTER_DOT_OVERLAY_RADIUS,
  SCATTER_DOT_RADIUS,
  Y_AXIS_LABEL,
} from '@/summaryReport/summaryData.constants';
import type Highcharts from 'highcharts';
import i18n from 'i18next';
import { CHART_FONT_STYLE } from '@/chart/chart.constants';

export const truncateValue = (value: number, precision = 4): number => {
  if (!Number.isFinite(value) || Number.isInteger(value)) return value;
  return parseFloat(value.toPrecision(precision));
};

export const getPropertyItems = (property: string, items: StatisticsSummaryItem[]) => {
  const propertyMap: Record<string, string | number | undefined> = { statistic: property, id: property };
  items.forEach((item: StatisticsSummaryItem) => {
    propertyMap[item.name] = truncateValue(_.get(item.data.stats, property, undefined));
  });
  return propertyMap;
};

export const hasNoData = (item: StatisticsSummaryItem): boolean => !item.data?.stats || item.data.stats.count === 0;

export const hasError = (item: StatisticsSummaryItem): boolean => !!item.error?.message;

export function extractErrorMessage(error: any): string {
  return (
    error?.response?.data?.statusMessage ||
    error?.response?.data?.message ||
    i18n.t('INVESTIGATE_TOOLS.SUMMARY_REPORT.UNKNOWN_ERROR')
  );
}

export const adjustColor = (color: string, percent = 50, lighten = true): string => {
  const num = parseInt(color.replace('#', ''), 16);

  if (lighten) {
    const r = Math.min(255, Math.floor((num >> 16) + (255 - (num >> 16)) * (percent / 100)));
    const g = Math.min(255, Math.floor(((num >> 8) & 0x00ff) + (255 - ((num >> 8) & 0x00ff)) * (percent / 100)));
    const b = Math.min(255, Math.floor((num & 0x0000ff) + (255 - (num & 0x0000ff)) * (percent / 100)));

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  } else {
    const r = Math.max(0, Math.floor((num >> 16) * (1 - percent / 100)));
    const g = Math.max(0, Math.floor(((num >> 8) & 0x00ff) * (1 - percent / 100)));
    const b = Math.max(0, Math.floor((num & 0x0000ff) * (1 - percent / 100)));

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  }
};

export const calculateY = (x: number, mean: number, stdDev: number): number => {
  const coefficient = 1 / (stdDev * Math.sqrt(2 * Math.PI));
  const exponent = -Math.pow(x - mean, 2) / (2 * Math.pow(stdDev, 2));
  return coefficient * Math.exp(exponent);
};

export const NUM_BELL_CURVE_POINTS = 81;

export const generatePoints = (
  average: number,
  stdDev: number,
  lower: number,
  upper: number,
  count: number,
  size = 1,
  yPlotUnit: Y_AXIS_LABEL = Y_AXIS_LABEL.COUNT,
): { x: number; y: number }[] => {
  if (count === 0) return [];
  const start = lower - size;
  const end = upper + size;
  const step = (end - start) / NUM_BELL_CURVE_POINTS;
  const points = _.range(start, end, step);
  return points.map((x) => {
    let y = calculateY(x, average, stdDev);
    if (yPlotUnit === Y_AXIS_LABEL.COUNT) {
      y = y * count * size;
    } else if (yPlotUnit === Y_AXIS_LABEL.PERCENTAGE) {
      y = count !== 0 ? y * size * 100 : 0;
    }
    return { x, y };
  });
};

export const fetchStatsSummaryData = async (id: string) => {
  // TODO: Replace experimental endpoint with a stable endpoint CRAB-53818
  const { data } = await sqFormulasApi.experimental({ formula: '$stats', parameters: [`stats=${id}`] });
  return data;
};

export function destroySVGElement(element: Highcharts.SVGElement | undefined) {
  element?.destroy();
}

function renderText(chart: Highcharts.Chart, text: string, xPixel = 0, yPixel = 0, color = 'black') {
  return chart.renderer
    .text(text, xPixel, yPixel)
    .attr({
      zIndex: BOXPLOT_LABEL_Z_INDEX,
      padding: 2,
    })
    .css({
      ...CHART_FONT_STYLE,
      fontWeight: 'bold',
      fontSize: 'x-small',
      textOutline: '1px white',
      color,
    })
    .add();
}

export function drawPointLabel(
  chart: Highcharts.Chart,
  point: BoxPlotPoint,
  seriesIndex: number,
  lastPosition = { x: -Infinity, offset: 0 },
) {
  if (_.isFinite(point.yPixel) && point.labelText) {
    const translatedText = i18n.t(point.labelText);
    const tempText = renderText(chart, translatedText);
    const labelWidth = tempText.getBBox().width;
    tempText.destroy();
    const labelText = `<span style="border-color: ${point.color}">${translatedText}</span>`;
    const xPixel = chart.yAxis[0].toPixels(point.xValue) - labelWidth / 2;
    let yPixel = chart.xAxis[0].toPixels(seriesIndex) + lastPosition.offset + CURSOR_Y_OFFSET;
    if (xPixel < lastPosition.x + labelWidth) {
      yPixel += CURSOR_Y_OFFSET;
    }
    point.labelOffset = lastPosition.offset;
    point.lastX = xPixel;
    point.label = renderText(chart, labelText, xPixel, yPixel, point.color);
  }
  return point;
}

function createPointData(property: string, value: number, unit: string, color: string) {
  return {
    label: undefined,
    xValue: value,
    yPixel: value,
    color,
    labelText: property,
    unitOfMeasure: { value: unit, fromSourceUom: false, isDisplay: false },
  };
}

const getBoxPlotData = (item: StatisticsSummaryItem) => {
  if (item?.data) {
    return [
      {
        name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MIN_WHISKER',
        value: item.data.stats.minWhisker,
      },
      { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.Q1', value: item.data.stats.quartile1 },
      { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MEDIAN', value: item.data.stats.quartile2 },
      { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.Q3', value: item.data.stats.quartile3 },
      {
        name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MAX_WHISKER',
        value: item.data.stats.maxWhisker,
      },
    ];
  }
  return [
    { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MIN_WHISKER', value: 0 },
    { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.Q1', value: 0 },
    { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.Q2', value: 0 },
    { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.Q3', value: 0 },
    { name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MAX_WHISKER', value: 0 },
  ];
};

export function getLabelPoints(items: StatisticsSummaryItem[]) {
  const pointMap: Record<string, BoxPlotPoint[]> = {};
  items.forEach((item) => {
    pointMap[item.id] = getBoxPlotData(item).map((d) => createPointData(d.name, d.value, item.data.uom, item.color));
  });
  return pointMap;
}

export const getBoxWidth = (chart: Highcharts.Chart, idx: number) => {
  const elements = chart.container.getElementsByClassName('highcharts-boxplot-box');
  if (elements.length >= idx + 1) {
    const element = elements[idx];

    // Return height cause chart is inverted
    return element.getBoundingClientRect().height;
  }
  return 0;
};

export function binValues(values: number[], binWidth: number) {
  if (!values || values.length === 0 || binWidth <= 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const bins = [];
  for (let start = min; start < max; start += binWidth) {
    const end = start + binWidth;
    bins.push({
      binStart: start,
      binEnd: end,
      values: values.filter((v) => v >= start && v < end),
    });
  }
  return bins;
}

export function generateScatterFromBins(
  item: StatisticsSummaryItem,
  idx: number,
  chart: Highcharts.Chart,
  isOverlay: boolean,
) {
  const scatterPointRadius = isOverlay ? SCATTER_DOT_OVERLAY_RADIUS : SCATTER_DOT_RADIUS;
  const scatterRadiusWithSpacing = scatterPointRadius + 1;
  const maxPointsOnLane = Math.round(getBoxWidth(chart, idx) / scatterRadiusWithSpacing);
  const xAxis = chart.xAxis[0];
  const boxCenter = xAxis.toPixels(idx);
  const yAxis = chart.yAxis[0];
  const binWidth = yAxis.toValue(scatterRadiusWithSpacing) - yAxis.toValue(0);
  const outlierBins = binValues(item.data.stats.outliers, binWidth);
  const scatterPoints: ScatterPoint[] = [];
  let pixelX = -Infinity;
  outlierBins.forEach((bin) => {
    for (let i = 0; i < bin.values.length; i++) {
      const laneIndex = Math.floor(i / maxPointsOnLane);
      const multiplier = i % maxPointsOnLane;
      const middleOffset = i < 2 ? 1 : 0;
      pixelX =
        pixelX < boxCenter
          ? boxCenter + middleOffset + laneIndex + scatterRadiusWithSpacing * multiplier
          : boxCenter - middleOffset - laneIndex - scatterRadiusWithSpacing * multiplier;
      const x = xAxis.toValue(pixelX);
      const y = bin.values[i];
      scatterPoints.push({
        x,
        y,
        color: item.color,
        marker: { symbol: 'circle', radius: scatterPointRadius },
        uom: item.data.uom,
      });
    }
  });
  return scatterPoints;
}
